/** Strings of the roadmap page (/full-de-ruta) and of its card on the admin page's integrations tab. */
export const ca_roadmap = {
  roadmapPage: {
    title: 'Full de ruta',
    intro: 'Què s’ha fet, què s’està fent i què ve. Comenta qualsevol punt o proposa’n un de nou.',
    comingUp: 'Les funcions que ens falten, una per una',
    updated: (when: string | null, version: string) =>
      when ? `Actualitzat el ${when} · versió ${version}` : `Versió ${version}`,
    error: 'No s’ha pogut carregar el full de ruta.',
    empty: 'Encara no hi ha res escrit al full de ruta.',
    now: 'Ara mateix',
    figures: 'Fet fins ara',
    // `testing` is only passed for the headline figure; a stage's own figure keeps «N de M punts fets».
    progress: (done: number, total: number, testing = 0) =>
      testing > 0
        ? `${done} fets i ${testing} en proves, de ${total}`
        : `${done} de ${total} punts fets`,
    progressLabel: 'Progrés de tot el full de ruta',
    track: 'Etapes',
    stageSummary: (when: string, total: number, done: number) =>
      `${when ? `${when} · ` : ''}${total} ${total === 1 ? 'punt' : 'punts'} · ${done} de ${total} fets`,
    states: {
      fet: 'Fet',
      'en-proves': 'En proves',
      'en-curs': 'En curs',
      comencat: 'Començat',
      seguent: 'Següent',
      'mes-endavant': 'Més endavant',
    } as Record<string, string>,
    /** The legend of the overall bar: everything that is neither done, in tests, under way nor started. */
    rest: 'La resta',

    comment: 'Comenta',
    comments: (n: number) => (n === 1 ? '1 comentari' : `${n} comentaris`),
    /** Beside an item's comment button: how many people tested it on the running version. */
    testedBy: (n: number) => `provat per ${n}`,
    testedByTitle: (n: number) =>
      n === 1
        ? '1 persona l’ha provat a la versió actual, amb una fitxa de prova tancada'
        : `${n} persones l’han provat a la versió actual, amb fitxes de prova tancades`,
    threadOf: (text: string) => `Comentaris de: ${text}`,
    threadLoading: 'Carregant els comentaris…',
    threadError: 'No s’han pogut carregar els comentaris.',
    threadEmpty: 'Encara no hi ha cap comentari. Sigues el primer.',
    write: 'Escriu un comentari',
    send: 'Comenta',
    sendAsProposal: 'Envia-ho com a proposta',
    proposal: 'Proposta',
    accepted: 'Incorporada al full de ruta',
    edited: 'editat',
    edit: 'Edita',
    remove: 'Esborra',
    removeConfirm: 'Segur que vols esborrar aquest missatge?',
    save: 'Desa',
    cancel: 'Cancel·la',
    someone: 'Algú',
    counter: (n: number, max: number) => `${n} de ${max}`,

    proposeForStage: 'Proposa un punt per a aquesta etapa',
    stageProposals: (n: number) => (n === 1 ? '1 proposta' : `${n} propostes`),
    stageProposalsOf: (title: string) => `Propostes per a l’etapa ${title}`,
    proposeWrite: 'Escriu la proposta',
    proposeSend: 'Envia la proposta',

    recipes: 'Receptes de missió',
    recipesIntro: 'Cada tipus de missió i com està ara mateix.',
    recipeStates: {
      ready: 'A punt',
      testing: 'En proves',
      coming: 'Properament',
      idea: 'Idea',
    } as Record<string, string>,
    decisions: 'Decisions preses',
    idea: 'Tens una idea que no és a la llista?',
    ideaIntro: 'Explica-la aquí. La veurà la resta d’usuaris i la podran comentar.',
    ideaWrite: 'La teva idea',
    ideaList: 'Idees enviades',

    just: 'ara mateix',
    minutesAgo: (n: number) => (n === 1 ? 'fa 1 minut' : `fa ${n} minuts`),
    hoursAgo: (n: number) => (n === 1 ? 'fa 1 hora' : `fa ${n} hores`),
    daysAgo: (n: number) => (n === 1 ? 'ahir' : `fa ${n} dies`),

    errors: {
      empty: 'Escriu alguna cosa abans d’enviar-ho.',
      too_long: 'El text és massa llarg: 2.000 caràcters com a màxim.',
      too_many: 'Has enviat molts missatges seguits. Espera un minut i torna-ho a provar.',
      unknown_item: 'Aquest punt ja no és al full de ruta. Torna a carregar la pàgina.',
      unknown_stage: 'Aquesta etapa ja no és al full de ruta. Torna a carregar la pàgina.',
      not_yours: 'Aquest missatge no és teu.',
      not_found: 'Aquest missatge ja no hi és.',
      other: 'No s’ha pogut desar. Torna-ho a provar.',
    } as Record<string, string>,

    admin: {
      only: 'Només ho veus tu',
      accept: 'Accepta-la com a punt nou',
      deny: 'Denega-la',
      undo: 'Desfés',
      decided: {
        ACCEPTED: 'L’has acceptada',
        DENIED: 'L’has denegada',
      } as Record<string, string>,
      note: 'Nota interna',
      noteSave: 'Desa la nota',
      noteSaved: 'Nota desada',
      filed: 'Ja és al fitxer',
      markFiled: 'Marca-la com a afegida al fitxer',
      unmarkFiled: 'Encara no és al fitxer',
      review: (n: number) => `Per revisar (${n})`,
      reviewTitle: 'Propostes per revisar',
      reviewEmpty: 'No hi ha cap proposta per revisar.',
      unfiled: (n: number) => `Acceptades, encara no al fitxer (${n})`,
      unfiledTitle: 'Propostes acceptades que encara no són al fitxer',
      unfiledEmpty: 'Totes les propostes acceptades ja són al fitxer.',
      retired: (n: number) => `Punts retirats (${n})`,
      retiredTitle: 'Comentaris de punts que ja no són al full de ruta',
      retiredEmpty: 'No hi ha cap comentari de punts retirats.',
      where: {
        item: (id: string) => `Al punt «${id}»`,
        stage: (slug: string) => `Per a l’etapa «${slug}»`,
        general: 'Idea general',
      },
      problems: 'El fitxer té línies que s’han hagut d’interpretar:',
    },
  },

  roadmapSource: {
    card: 'Full de ruta',
    intro:
      'La pàgina del full de ruta llegeix el fitxer d’un repositori de GitHub cada cinc minuts. Sense configurar, ensenya la còpia que porta la versió instal·lada.',
    repository: 'Repositori',
    repositoryHint: 'En la forma propietari/nom.',
    branch: 'Branca',
    path: 'Camí del fitxer',
    token: 'Clau de GitHub',
    tokenHint:
      'N’hi ha prou amb una clau detallada (fine-grained) amb permís de només lectura de «Contents» sobre aquest únic repositori. S’escriu i no es torna a ensenyar mai.',
    tokenSet: (when: string | null) => (when ? `Clau desada (${when})` : 'Clau desada'),
    tokenUnset: 'Sense clau',
    save: 'Desa',
    saved: 'Desat',
    forget: 'Treu la clau',
    forgetConfirm: 'Segur que vols treure la clau? El full de ruta deixarà d’actualitzar-se.',
    refresh: 'Actualitza ara',
    refreshing: 'Actualitzant…',
    status: 'Estat',
    serving: {
      fetched: 'Ensenyant la còpia llegida del repositori',
      bundled: 'Ensenyant la còpia de la versió instal·lada',
    },
    lastCheck: 'Última comprovació',
    lastChange: 'Últim canvi',
    lastError: 'Últim error',
    never: 'mai',
    commit: (sha: string, when: string | null) => (when ? `${sha} · ${when}` : sha),
    results: {
      updated: 'S’ha llegit una còpia nova del fitxer.',
      unchanged: 'El fitxer no ha canviat.',
    } as Record<string, string>,
    errors: {
      unauthorized: 'GitHub no accepta la clau.',
      not_found: 'No trobo el repositori, la branca o el fitxer.',
      rate_limited: 'GitHub diu que hem fet massa peticions. Es tornarà a provar més tard.',
      timeout: 'GitHub ha trigat massa a respondre.',
      unreachable: 'No s’ha pogut arribar a GitHub.',
      too_large: 'El fitxer és massa gros (més de 256 KB).',
      no_stage: 'El fitxer no té cap etapa: no sembla un full de ruta. Es manté la còpia anterior.',
      http_error: 'GitHub ha respost amb un error. Es manté la còpia anterior.',
      not_configured: 'Cal un repositori i una clau per poder actualitzar.',
      bad_repository: 'El repositori ha de ser de la forma propietari/nom.',
      bad_branch: 'El nom de la branca no és vàlid.',
      bad_path: 'El camí ha de ser un fitxer .md del repositori, sense «..».',
      bad_token: 'Això no sembla una clau de GitHub.',
      other: 'No s’ha pogut desar.',
    } as Record<string, string>,
  },
};
